import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useHomecareRooms } from "@/hooks/useHomecareRooms";
import RoomChatButton from "@/components/RoomChatButton";
import { formatDateLong, waLink, todayWITA } from "@/lib/constants";
import { StatusBadge, ServiceBadge, PaymentBadge } from "@/components/Badges";
import DetailDialog from "@/components/DetailDialog";
import { CalendarCheck, WhatsappLogo, Clock, Eye, CalendarBlank, CheckCircle, Prohibit, ArrowRight, FolderOpen } from "@phosphor-icons/react";

const CANCELLED = ["Batal", "No Show", "Dibatalkan Otomatis", "Dibatalkan", "Kedaluwarsa"];

export default function TodaySchedule() {
  const { user } = useAuth();
  const isPetugas = user?.role === "petugas";
  const { byReservation } = useHomecareRooms();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [occ, setOcc] = useState(null);
  const navigate = useNavigate();

  const load = () => {
    api.get("/schedule/today", { params: { date: todayWITA(), include_cancelled: true } }).then((r) => {
      setItems(r.data);
      setLoading(false);
    });
    // Same canonical availability engine as the calendar / booking.
    api.get(`/calendar?date=${todayWITA()}`).then((r) => setOcc(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openRme = async (rid) => {
    try { const { data } = await api.get(`/rme/encounter-by-reservation/${rid}`); navigate(`/rekam-medis/${data.encounter_id}`); }
    catch (e) { toast.error(apiError(e)); }
  };

  const active = items.filter((r) => !CANCELLED.includes(r.status));
  const cancelled = items.filter((r) => CANCELLED.includes(r.status));
  const staffNames = (occ?.staff || []).map((s) => s.nama);
  const reservasiCount = (occ?.reservations || []).filter((r) => staffNames.includes(r.petugas_nama) && !CANCELLED.includes(r.status)).length;
  let slotKosong = 0;
  (occ?.staff || []).forEach((s) => { const m = occ.slot_status?.[s.nama] || {}; slotKosong += Object.values(m).filter((v) => v.bookable).length; });
  const blokirAktif = (occ?.blocked || []).filter((b) => staffNames.includes(b.petugas_nama)).length;

  return (
    <div className="animate-fade-up space-y-6">
      <div>
        <div className="flex items-center gap-2">
          <CalendarCheck size={26} weight="duotone" className="text-[#7C4DE0]" />
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-800">Jadwal Hari Ini</h1>
        </div>
        <p className="mt-1 text-sm text-slate-500">{formatDateLong(todayWITA())} · {active.length} pasien</p>
      </div>

      {occ && (occ.staff || []).length > 0 && (
        <div data-testid="my-schedule-summary" className="rounded-2xl border border-[#E0D2FF] bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <p className="font-heading text-sm font-bold text-slate-700">Ringkasan Jadwal Saya Hari Ini</p>
            <button data-testid="open-full-calendar" onClick={() => navigate("/kalender")} className="inline-flex items-center gap-1 text-xs font-bold text-[#7C4DE0] hover:underline">Buka Kalender <ArrowRight size={13} weight="bold" /></button>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <SummaryStat testid="sum-reservasi" icon={CheckCircle} color="#7C4DE0" bg="#EEE6FF" value={reservasiCount} label="Reservasi" />
            <SummaryStat testid="sum-kosong" icon={CalendarBlank} color="#047857" bg="#d1fae5" value={slotKosong} label="Slot Kosong" />
            <SummaryStat testid="sum-blokir" icon={Prohibit} color="#334155" bg="#e2e8f0" value={blokirAktif} label="Blokir Aktif" />
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-rose-200 border-t-rose-500" />
        </div>
      ) : active.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-rose-200 bg-white/50 p-12 text-center text-sm text-slate-500">
          Belum ada reservasi untuk hari ini.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {active.map((r) => (
            <div key={r.id} data-testid={`schedule-card-${r.id}`} className="rounded-2xl border border-rose-100/70 bg-white p-5 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="flex flex-col items-center rounded-2xl bg-[#7C4DE0] px-4 py-3 text-white">
                  <Clock size={16} weight="bold" />
                  <span className="mt-1 font-heading text-lg font-extrabold leading-none">{r.jam_kunjungan}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-heading text-lg font-bold text-slate-800">{r.nama_lengkap}</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <ServiceBadge service={r.jenis_layanan} />
                    <StatusBadge status={r.status} />
                    <PaymentBadge status={r.payment_status} />
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                    <Info label="Petugas" value={r.petugas_nama || "-"} />
                    {!isPetugas && <Info label="No. WhatsApp" value={r.no_whatsapp || "-"} />}
                    <Info label="Usia Kehamilan" value={r.usia_kehamilan_text || (r.usia_kehamilan_minggu ? `${r.usia_kehamilan_minggu} minggu` : "-")} />
                    <Info label="Kehamilan ke-" value={r.kehamilan_ke ?? "-"} />
                  </div>
                  {r.keluhan ? (
                    <p className="mt-2 rounded-lg bg-[#F7F0FF] px-3 py-2 text-sm text-slate-600">
                      <span className="font-semibold text-[#6A38C7]">Keluhan:</span> {r.keluhan}
                    </p>
                  ) : null}
                  {r.lokasi === "Homecare" ? (
                    <div data-testid={`schedule-homecare-${r.id}`} className="mt-2 rounded-lg border border-pink-100 bg-pink-50 px-3 py-2 text-sm text-slate-700">
                      <p className="font-bold text-[#DB2777]">🏠 Homecare</p>
                      <p className="mt-0.5"><span className="font-semibold">Alamat:</span> {r.alamat || "-"}</p>
                      {r.patokan ? <p><span className="font-semibold">Patokan:</span> {r.patokan}</p> : null}
                      {!isPetugas && <p><span className="font-semibold">HP:</span> {r.no_whatsapp || "-"}</p>}
                      {r.maps_link ? <a href={r.maps_link} target="_blank" rel="noreferrer" className="font-semibold text-[#DB2777] underline">Buka Google Maps</a> : null}
                    </div>
                  ) : null}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <RoomChatButton reservation={r} roomInfo={byReservation[String(r.id)]} size="md" />
                    {!isPetugas && (
                      <a
                        href={waLink(r.no_whatsapp, `Halo ${r.nama_lengkap}, mengingatkan jadwal Anda hari ini pukul ${r.jam_kunjungan} di GBS Clinic.`)}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`schedule-wa-${r.id}`}
                        className="inline-flex items-center gap-2 rounded-xl bg-[#25D366] px-4 py-2 text-sm font-bold text-white transition-transform active:scale-95"
                      >
                        <WhatsappLogo size={18} weight="fill" /> Hubungi
                      </a>
                    )}
                    <button
                      onClick={() => setDetail(r)}
                      data-testid={`schedule-detail-${r.id}`}
                      className="inline-flex items-center gap-2 rounded-xl border border-[#7C4DE0] px-4 py-2 text-sm font-bold text-[#7C4DE0] transition-colors hover:bg-[#F7F0FF] active:scale-95"
                    >
                      <Eye size={18} weight="bold" /> Lihat Detail
                    </button>
                    {r.status === "Sudah Datang" && (
                      <button
                        onClick={() => openRme(r.id)}
                        data-testid={`today-open-rme-${r.id}`}
                        className="inline-flex items-center gap-2 rounded-xl bg-[#7C4DE0] px-4 py-2 text-sm font-bold text-white transition-transform active:scale-95"
                      >
                        <FolderOpen size={18} weight="fill" /> Buka Rekam Medis
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {!loading && cancelled.length > 0 && (
        <div data-testid="today-cancelled-section" className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">Dibatalkan ({cancelled.length})</p>
          <div className="space-y-1">
            {cancelled.map((r) => (
              <button key={r.id} data-testid={`today-cancelled-${r.id}`} onClick={() => setDetail(r)} className="flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-left text-sm text-slate-500 hover:bg-slate-100">
                <span className="truncate">{r.jam_kunjungan} · {r.nama_lengkap} · {r.jenis_layanan}</span>
                <span className="ml-2 shrink-0 text-xs font-semibold">{r.status}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <DetailDialog reservation={detail} open={!!detail} onClose={() => setDetail(null)} onChanged={load} />
    </div>
  );
}

function SummaryStat({ testid, icon: Icon, color, bg, value, label }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-100 bg-slate-50/60 p-3 text-center">
      <span className="mx-auto mb-2 flex h-9 w-9 items-center justify-center rounded-lg" style={{ backgroundColor: bg, color }}>
        <Icon size={18} weight="duotone" />
      </span>
      <p className="font-heading text-2xl font-extrabold text-slate-800">{value}</p>
      <p className="mt-0.5 text-[11px] font-medium text-slate-500">{label}</p>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-xs text-slate-400">{label}</p>
      <p className="font-semibold text-slate-700">{value}</p>
    </div>
  );
}
